import { createContext, ReactNode, useCallback, useMemo } from 'react';
import { useState } from 'react';
import { authManager } from 'services/authManager';
import { Session } from 'types/user';
import { User } from 'types/user';

interface SessionProviderProps {
	children: ReactNode;
}

export interface SessionContextData {
	session: Session;
	updateSession: (session: User) => void;
	startSession: (session: Session) => void;
	removeSession: () => void;
}

export const SessionContext = createContext<SessionContextData>({} as SessionContextData);

export const SessionProvider = ({ children }: SessionProviderProps) => {
	const [session, setSession] = useState<Session>({} as Session);

	const updateSession = useCallback(
		(data: User) => {
			setSession({ ...session, ...data });
			authManager.set({ ...session, ...data });
		},
		[setSession, session],
	);

	const startSession = useCallback(
		(session: Session) => {
			setSession({
				...session,
			});
			authManager.set(session);
		},
		[setSession],
	);

	const removeSession = useCallback(() => {
		setSession({} as Session);
		authManager.clear();
	}, [setSession]);

	const contextValue = useMemo(
		() => ({
			session,
			updateSession,
			startSession,
			removeSession,
		}),
		[updateSession, removeSession, startSession, session],
	);

	return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
};

export default SessionProvider;
